import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { Location } from '@angular/common';
import { OvsComponent, ClientVersion, QueryParamService } from '../shared/core/ovs.core';
import { ResortApplicationBase } from '../services/resort-application.service';
import { ResortDetailClientBase } from '../services/resort-detail.client';
import { ResortDetail, ResortInventory, ResortImageType, SessionValidationResult, ResortImage } from '../models/presentation.model';
import { ResortInventoryGroup } from '../models/browser.model';
import { PageScrollConfig } from 'ngx-page-scroll';
import { Geometry, Coordinates, CoordinateType, Bounds } from '../shared/geo/ovs.geocoding';
import { ResortComponent } from '../shared/resort.component';
import { MatDialog, MatDialogRef } from '@angular/material';
import { RoomAvailabilityComponent } from '../resort-detail/room-availability/room-availability.component';
import { ApplicationConfigurationClientBase } from '../services/application-configuration.client';
import { CookieService } from '@ngx-toolkit/cookie';
import { UpdateClientService } from '../services/update-client.service';
import { StorageService } from '../services/storage-service.client';
import { ResortDetailsPageEvents, ResortPageViews } from '../instrumentation/resort-custom-events';
import { Router } from '@angular/router';
import { ResortProperties } from '../instrumentation/tealium/tealium-data-points.model';
import { UserActivityLogger, UserActivityPublishDataTypes, UserActivityLoggerOptions } from '../instrumentation/user-activity-logger.service';
import { ImageService } from '../services/image.service';
@Component({
    selector: 'resort-standard',
    templateUrl: './resort-standard.component.html',
    styleUrls: ['./resort-standard.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class ResortStandardComponent extends ResortComponent {
    private _resortClient: ResortDetailClientBase;
    private _dialog: MatDialog;
    private _roomDialog: MatDialogRef<RoomAvailabilityComponent, ResortInventory>;

    public resort: ResortDetail;
    public group: ResortInventoryGroup;
    public mapGeometry: Geometry;
    public useBrowserBackOverride: boolean;

    public get isRciInventory(): boolean {
        return this.resort.id.indexOf(':') == 1;
    }

    constructor(private router: Router, resortClient: ResortDetailClientBase, dialog: MatDialog, private updateClientService: UpdateClientService
        , private storageService: StorageService, private qryParamService: QueryParamService, private imageService: ImageService, private location: Location) {
        super();
        this._dialog = dialog;
        this._resortClient = resortClient;
        PageScrollConfig.defaultScrollOffset = 125;
        PageScrollConfig.defaultDuration = 500;
    }

    handleErr(e) {
        this.mapAppInsightsInfo(e);
    }

    protected initialize(): void {
        super.initialize();
        window.scroll(0, 0);

        let resortId: string = this.qryParamService.resortDetailParams.resortId;
        let startDate: Date = this.qryParamService.resortDetailParams.startDate;
        var self = this;
        this._resortClient.Get(resortId, startDate, v => this.onGetResortDetailCompleted(v), self.handleErr.bind(this));

        this.useBrowserBackOverride = this.configuration.UseBrowserBackOnResortDetailsPage;
    }

    public get hasDiscount(): boolean {
        return this.group && this.group.featuredInventory && this.group.featuredInventory.hasDiscount;
    }

    public get memberRate(): number {
        return this.group && this.group.featuredInventory && this.group.featuredInventory.getMemberRate(this.configuration.ShowPricePerDay);
    }

    private onGetResortDetailCompleted(resortDetail: ResortDetail): void {
        this.qryParamService.resortListingParams.isDirect = false;

        this.resort = resortDetail;
        let geo: Geometry = new Geometry();
        geo.Coordinates = new Coordinates(this.resort.latitude, this.resort.longitude, CoordinateType.Center);
        this.mapGeometry = geo;
        let minDiscountedPrice: number = this.resort.resortInventoryList.min(i => i.hasDiscount ? i.dailyDiscountedPrice : i.dailyPrice);
        this.group = new ResortInventoryGroup(resortDetail, resortDetail.resortInventoryList);
        UserActivityLogger.publish(this.application.configuration, UserActivityPublishDataTypes.ApplicationConfiguration);
        UserActivityLogger.publish(this.configuration.Member, UserActivityPublishDataTypes.Member);
        UserActivityLogger.publish(this.resort, UserActivityPublishDataTypes.ResortDetail);
        UserActivityLogger.logPageView(ResortPageViews.ResortDetailsStandard, UserActivityLoggerOptions.Both, {
            memberRate: this.memberRate
        });
    }


    public book(inventory: ResortInventory): void {
        this.updateClientService.EnsureLatestVersion(() => {
            this.application.checkOut(this.resort, inventory);
        });
    }

    public bookWithUpgrade(inventory: any): void {
        this.application.checkOut(this.resort, <ResortInventory>inventory, true);
    }

    public showCompleteAvailability(): void {
        UserActivityLogger.logEvent(ResortDetailsPageEvents.CarouselAvilabilityButtonClicked, {}, UserActivityLoggerOptions.AppInsightsOnly);
        this._roomDialog = this._dialog.open(RoomAvailabilityComponent, { data: this.group });
        this._roomDialog.afterClosed().subscribe(inventory => {
            if (inventory)
                this.book(inventory);
        });
        this.updateClientService.EnsureLatestVersion(() => { });
    }

    public goBrowserHistoryBack() {
        const logoNavigationUrl = this.configuration.Logos ? this.configuration.Logos.first().NavigationUrl : "";
        if (logoNavigationUrl === "") {
            this.location.back();
        } else {
            window.location.href = logoNavigationUrl;
        }
    }
    public goBack() {
        this.router.navigate(["search-resorts"], { queryParams: { sid: this.qryParamService.encodedGuid } });
    }

    public get hasEnoughImagesForCollage(): boolean {
        let images = this.imageService.getImagesByType(this.resort.resortImageList.where(i => i.fileName.substr(4, 1) != 'F').toArray(), [])

        let dedupedImages = new Array<ResortImage>();

        images.forEach(i => {
            if (dedupedImages.where(d => d.title === i.title).count() === 0) {
                dedupedImages.add(i);
            }
        });

        return dedupedImages.length >= 8;
    }

    private mapAppInsightsInfo(ex) {
        const mapData = new Map();
        mapData.set("memberid", this.application.configuration.Member.Id);
        mapData.set("ovcpartner", this.application.configuration.Partner.LongName);
        mapData.set("memberclass", this.application.configuration.MemberClass);
        mapData.set("product", "Resort");
        mapData.set("errormessage", ex.message ? ex.message : "Details exception unknown");
        mapData.set("httperrorcode", ex.code ? ex.code : ex.status ? ex.status : '');
        mapData.set("origination", "ICE");
        mapData.set("resortdetailfailed", JSON.stringify(ex));
        let jsonObject = {};
        mapData.forEach((value, key) => {
            jsonObject[key] = value;
        });

        UserActivityLogger.logEvent(ResortDetailsPageEvents.ResortDetailFailure, jsonObject, UserActivityLoggerOptions.AppInsightsOnly);
    }
}


// WEBPACK FOOTER //
// /home/vsts/work/1/s/Ovs.Resort.UI.Web/src/app/resort-standard/resort-standard.component.ts